import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Divider,
  Paper,
  Chip,
  Stack,
  ListItemButton,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import { useForm, Controller } from "react-hook-form";
import { API_ENDPOINT } from "../../utils/constants";
import { getHeader, toastError } from "../../utils/util";
import { toast } from "react-toastify";
import { useDialog } from "../../context/DialogContext";
import BackBtn from "../../widgets/BackBtn";
import { useNavigate } from "react-router-dom";
import { UserContext } from "../../context/UserProvider";
import { GlassButton } from "../../themes/UiElements";
const CategoryManager = () => {
  const { showDialog } = useDialog();
  const navigate = useNavigate();
  const { user, loading } = useContext(UserContext);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [openCategoryDialog, setOpenCategoryDialog] = useState(false);
  const [openSubcategoryDialog, setOpenSubcategoryDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingSubcategory, setEditingSubcategory] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [columnsInput, setColumnsInput] = useState(""); // State for raw input

  const {
    control: categoryControl,
    handleSubmit: handleCategorySubmit,
    reset: resetCategoryForm,
    formState: { errors: categoryErrors },
  } = useForm();

  const {
    control: subcategoryControl,
    handleSubmit: handleSubcategorySubmit,
    reset: resetSubcategoryForm,
    setValue: setSubcategoryValue, // Destructure setValue
    formState: { errors: subcategoryErrors },
  } = useForm({
    defaultValues: {
      Subcategory: "",
      columns: ["Phone", "Name"],
    },
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    if (selectedCategory) {
      fetchSubcategories(selectedCategory._id);
    }
  }, [selectedCategory]);

  useEffect(() => {
    // Update columnsInput when editing a subcategory
    if (editingSubcategory) {
      const additionalColumns = editingSubcategory.columns
        .filter((col) => col !== "Phone" && col !== "Name")
        .join(", ");
      setColumnsInput(additionalColumns);
    } else {
      setColumnsInput("");
    }
  }, [editingSubcategory]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(
        `${API_ENDPOINT}/user/contacts-categories`,
        getHeader()
      );
      setCategories(response.data);
      if (response.data.length > 0 && !selectedCategory) {
        setSelectedCategory(response.data[0]);
      }
    } catch (error) {
      toastError(error);
    }
  };

  const fetchSubcategories = async (categoryId) => {
    try {
      const response = await axios.get(
        `${API_ENDPOINT}/user/contacts-subcategories/${categoryId}`,
        getHeader()
      );
      setSubcategories(response.data);
    } catch (error) {
      toastError(error);
    }
  };

  const handleOpenCategoryDialog = (category = null) => {
    setEditingCategory(category);
    resetCategoryForm({
      name: category ? category.name : "",
    });
    setOpenCategoryDialog(true);
  };

  const handleOpenSubcategoryDialog = (subcategory = null) => {
    if (!selectedCategory) {
      alert("Please select a category first");
      return;
    }

    setEditingSubcategory(subcategory);
    resetSubcategoryForm({
      Subcategory: subcategory ? subcategory.name : "", // Map to 'name' field
      columns: subcategory ? subcategory.columns : ["Phone", "Name"],
    });
    setOpenSubcategoryDialog(true);
  };

  const handleCloseCategoryDialog = () => {
    setOpenCategoryDialog(false);
    setEditingCategory(null);
  };

  const handleCloseSubcategoryDialog = () => {
    setOpenSubcategoryDialog(false);
    setEditingSubcategory(null);
    setColumnsInput("");
  };

  const onSubmitCategory = async (data) => {
    try {
      const toSend = { ...data };
      if (editingCategory) {
        toSend.editId = editingCategory._id;
      }
      const response = await axios.post(
        `${API_ENDPOINT}/user/add-contacts-category`,
        toSend,
        getHeader()
      );
      toast.success(response.data.message);
      fetchCategories();
      handleCloseCategoryDialog();
    } catch (error) {
      toastError(error);
    }
  };

  const onSubmitSubcategory = async (data) => {
    try {
      const payload = {
        name: data.Subcategory,
        columns: data.columns,
      };
      if (editingSubcategory) {
        payload.editId = editingSubcategory._id;
      } else {
        payload.categoryId = selectedCategory._id;
        payload.category = selectedCategory.name;
      }
      const response = await axios.post(
        `${API_ENDPOINT}/user/add-contacts-subcategory`,
        payload,
        getHeader()
      );
      toast.success(response.data.message);
      fetchSubcategories(selectedCategory._id);
      handleCloseSubcategoryDialog();
    } catch (error) {
      toastError(error);
    }
  };

  const handleDeleteCategory = async (id) => {
    showDialog(
      "Please confirm",
      "Are you sure you want to delete this category and all its subcategories and its contacts?",
      {
        type: "delete",
        onConfirm: async () => {
          try {
            const response = await axios.delete(
              `${API_ENDPOINT}/user/delete-category/${id}`,
              getHeader()
            );
            toast.success(response.data.message);
            fetchCategories();
            if (selectedCategory && selectedCategory._id === id) {
              setSelectedCategory(null);
            }
          } catch (error) {
            toastError(error);
          }
        },
      }
    );
  };

  const handleDeleteSubcategory = async (id) => {
    showDialog(
      "Please confirm",
      "Are you sure you want to delete this subcategory and all its contacts?",
      {
        type: "delete",
        onConfirm: async () => {
          try {
            const response = await axios.delete(
              `${API_ENDPOINT}/user/delete-subcategory/${id}`,
              getHeader()
            );
            toast.success(response.data.message);
            fetchSubcategories(selectedCategory._id);
          } catch (error) {
            toastError(error);
          }
        },
      }
    );
  };

  const handleColumnsInputChange = (e) => {
    const input = e.target.value;
    setColumnsInput(input);
    const additionalColumns = input
      .split(",")
      .map((item) => item.trim())
      .filter((item) => item && item !== "Phone" && item !== "Name");
    setSubcategoryValue("columns", additionalColumns);
  };

  return (
    <Box
      sx={{
        display: "flex",
        height: (theme) =>
          `calc(100vh - ${theme.mixins.toolbar.minHeight + 4}px)`,
      }}
    >
      {/* Categories Panel */}
      <Paper sx={{ width: 300, p: 2, overflow: "auto" }}>
        <BackBtn />
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h6">Categories</Typography>
          <GlassButton
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => handleOpenCategoryDialog()}
          >
            Add
          </GlassButton>
        </Box>

        <List>
          {categories.map((category) => (
            <React.Fragment key={category._id}>
              <ListItemButton
                selected={selectedCategory?._id === category._id}
                onClick={() => setSelectedCategory(category)}
                sx={{ borderRadius: 1 }}
              >
                <ListItemText primary={category.name} />
                <Box>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenCategoryDialog(category);
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteCategory(category._id);
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </ListItemButton>
              <Divider />
            </React.Fragment>
          ))}
        </List>
      </Paper>

      {/* Subcategories Panel */}
      <Box sx={{ flex: 1, p: 3, overflow: "auto" }}>
        {selectedCategory ? (
          <>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
              }}
            >
              <Typography variant="h5">
                Subcategories for: {selectedCategory.name}
              </Typography>
              <GlassButton
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenSubcategoryDialog()}
              >
                Add Subcategory
              </GlassButton>
            </Box>

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
              {subcategories.map((subcategory) => (
                <Paper key={subcategory._id} sx={{ p: 2, width: 300 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography variant="h6">{subcategory.name}</Typography>
                    <Box>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenSubcategoryDialog(subcategory)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteSubcategory(subcategory._id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>

                  {user?.userType === "SuperAdmin" && (
                    <Stack direction={"row"} justifyContent={"end"}>
                      <GlassButton
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          navigate(`/home/${subcategory._id}/assign-user`);
                        }}
                        startIcon={<AssignmentIndIcon />}
                      >
                        Assign User
                      </GlassButton>
                    </Stack>
                  )}

                  <Typography variant="subtitle2" sx={{ mt: 1 }}>
                    Columns:
                  </Typography>
                  <Box
                    sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1 }}
                  >
                    {subcategory.columns.map((col) => (
                      <Chip key={col} label={col} size="small" />
                    ))}
                  </Box>
                </Paper>
              ))}
            </Box>
          </>
        ) : (
          <Typography variant="h6" sx={{ textAlign: "center", mt: 10 }}>
            {categories.length === 0
              ? "No categories found. Create your first category!"
              : "Select a category to view subcategories"}
          </Typography>
        )}
      </Box>

      <Dialog
        fullWidth
        maxWidth="sm"
        open={openCategoryDialog}
        onClose={handleCloseCategoryDialog}
      >
        <DialogTitle>
          {editingCategory ? "Edit Category" : "Add New Category"}
          <IconButton
            aria-label="close"
            onClick={handleCloseCategoryDialog}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box
            component="form"
            onSubmit={handleCategorySubmit(onSubmitCategory)}
            sx={{ mt: 1 }}
          >
            <Controller
              name="name"
              control={categoryControl}
              defaultValue=""
              rules={{ required: "Category name is required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  margin="normal"
                  fullWidth
                  label="Category Name"
                  error={!!categoryErrors.name}
                  helperText={categoryErrors.name?.message}
                />
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCategoryDialog}>Cancel</Button>
          <Button
            onClick={handleCategorySubmit(onSubmitCategory)}
            variant="contained"
          >
            {editingCategory ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openSubcategoryDialog}
        onClose={handleCloseSubcategoryDialog}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {editingSubcategory ? "Edit Subcategory" : "Add New Subcategory"}
          <IconButton
            aria-label="close"
            onClick={handleCloseSubcategoryDialog}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box
            component="form"
            onSubmit={handleSubcategorySubmit(onSubmitSubcategory)}
            sx={{ mt: 1 }}
          >
            <Controller
              name="Subcategory"
              control={subcategoryControl}
              defaultValue=""
              rules={{ required: "Subcategory name is required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  margin="normal"
                  fullWidth
                  label="Subcategory Name"
                  error={!!subcategoryErrors.Subcategory}
                  helperText={subcategoryErrors.Subcategory?.message}
                />
              )}
            />

            <Typography variant="subtitle1" sx={{ mt: 3 }}>
              Columns
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              (Phone and Name are mandatory)
            </Typography>

            <TextField
              margin="normal"
              fullWidth
              label="Additional Columns (comma separated)"
              placeholder="Email, Address, etc."
              value={columnsInput}
              onChange={handleColumnsInputChange}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSubcategoryDialog}>Cancel</Button>
          <Button
            onClick={handleSubcategorySubmit(onSubmitSubcategory)}
            variant="contained"
          >
            {editingSubcategory ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CategoryManager;
